'use client'

import type { ComponentProps, FC } from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { ActionMeta, MultiValue } from 'react-select'

import { AGENCY_TENANT } from '~/core/constants/enum'
import { AsyncMultipleSearchWithSelect } from '~/core/ui/AsyncMultipleSearchWithSelect'
import type { IPromiseSearchOption, ISelectOption } from '~/core/ui/Select'

import useDetectCompanyWithKind from '~/lib/hooks/use-detect-company-with-kind'

// Define the complete data structure interface
interface DataState {
  collection: ISelectOption[]
  parentChildsMap: { [key: string]: ISelectOption[] }
  parentMap: { [key: string]: ISelectOption | undefined }
  depMap: { [key: string]: ISelectOption }
  isSearch: boolean
}

type AsyncMultipleSearchWithSelectOptionGroupProps = ComponentProps<
  typeof AsyncMultipleSearchWithSelect
> & {
  selectAllChange?: (value: boolean) => void
  showSelectOptionAll: boolean
}

const AsyncMultipleSearchWithSelectOptionGroup: FC<
  AsyncMultipleSearchWithSelectOptionGroupProps
> = (props) => {
  const { t } = useTranslation()

  // Initialize with complete data structure
  const [data, setData] = useState<DataState>({
    collection: [],
    parentChildsMap: {},
    parentMap: {},
    depMap: {},
    isSearch: false
  })

  const [loaded, setLoaded] = useState(true)
  const dataRef = useRef(data)
  dataRef.current = data

  const { isCompanyKind } = useDetectCompanyWithKind({
    kind: AGENCY_TENANT
  })

  const promiseOptions = useCallback(
    (params: IPromiseSearchOption) => {
      if (!props.promiseOptions) {
        return Promise.resolve({
          collection: [] as ISelectOption[],
          metadata: { totalCount: 0 }
        })
      }

      const result = props.promiseOptions(params)
      const promise = result instanceof Promise ? result : Promise.resolve(result)

      return promise.then((result) => {
        const collection =
          (params.page ?? 1) > 1
            ? [...dataRef.current.collection, ...result.collection]
            : result.collection

        const parentChildsMap = collection.reduce(
          (acc, item) =>
            item.parentId
              ? {
                  ...acc,
                  [item.parentId]: [
                    ...(acc[item.parentId] || []).filter((fitem) => fitem.value !== item.value),
                    item
                  ]
                }
              : acc,
          dataRef.current.parentChildsMap || {}
        )

        const parentMap = Object.keys(parentChildsMap).reduce(
          (acc, key) => ({
            ...acc,
            [key]: collection.find((item) => item.value === key)
          }),
          dataRef.current.parentMap || {}
        )

        const depMap = collection.reduce(
          (acc, item) => ({ ...acc, [item.value]: item }),
          {} as { [key: string]: ISelectOption }
        )

        setData({
          parentMap,
          parentChildsMap,
          depMap,
          collection,
          isSearch: !!params.search
        })

        return {
          ...result,
          collection:
            (params.page ?? 1) > 1 || params.search
              ? result.collection
              : result.collection.length > 0
                ? props.showSelectOptionAll
                  ? [
                      {
                        value: '0',
                        isHideAvatar: true,
                        supportingObj: {
                          name: `${t('settings:departments:allDepartments')}`
                        }
                      } as ISelectOption,
                      ...result.collection
                    ]
                  : [...result.collection]
                : []
        } as { metadata?: { totalCount: number }; collection: ISelectOption[] }
      })
    },
    [props.promiseOptions, props.showSelectOptionAll, t]
  ) as NonNullable<AsyncMultipleSearchWithSelectOptionGroupProps['promiseOptions']>

  const onChange = useCallback(
    (value: MultiValue<ISelectOption>, actionMeta: ActionMeta<ISelectOption>) => {
      if (!props.onChange) return

      const childIds = data.parentChildsMap?.[actionMeta?.option?.value || ''] || []

      if (actionMeta.action === 'select-option') {
        if (actionMeta.option?.value === '0') {
          return props.onChange([...data.collection], actionMeta)
        }

        if (actionMeta.option?.parentId) {
          const totalChildSelect = value.filter(
            (item) => data.depMap[item.value]?.parentId === actionMeta.option?.parentId
          ).length
          const totalChilds = data.parentChildsMap?.[actionMeta.option?.parentId] || []

          if (totalChildSelect > 0 && totalChildSelect === totalChilds.length) {
            const parentOption = data.parentMap[actionMeta.option?.parentId]
            if (parentOption) {
              return props.onChange([...value, parentOption], actionMeta)
            }
          }
        }

        return props.onChange(
          [
            ...value.filter((item) => !childIds.map((child) => child.value).includes(item.value)),
            ...childIds
          ],
          actionMeta
        )
      }

      if (actionMeta.action === 'deselect-option') {
        const deselectOption = actionMeta.option
        if (deselectOption?.value === '0') {
          return props.onChange([], actionMeta)
        }
        if (deselectOption?.parentId) {
          return props.onChange(
            value.filter((item) => item.value !== '0' && item.value !== deselectOption?.parentId),
            actionMeta
          )
        }
        return props.onChange(
          value.filter(
            (item) =>
              item.value !== '0' && !childIds.map((child) => child.value).includes(item.value)
          ),
          actionMeta
        )
      }

      if (actionMeta.action === 'remove-value') {
        if (actionMeta.removedValue?.value === '0') {
          return props.onChange([], actionMeta)
        }
        const removedValuechildIds =
          data?.parentChildsMap?.[actionMeta?.removedValue?.value || ''] || []

        return props.onChange(
          value.filter(
            (item) =>
              item.value !== '0' &&
              !removedValuechildIds.map((child) => child.value).includes(item.value)
          ),
          actionMeta
        )
      }

      return props.onChange(value, actionMeta)
    },
    [data, props.onChange]
  ) as NonNullable<AsyncMultipleSearchWithSelectOptionGroupProps['onChange']>

  const shortTerningValue = useCallback(
    (valuesVariable: ISelectOption[] | undefined): ISelectOption[] => {
      const values = valuesVariable || []

      const cpMap = values.reduce(
        (acc, item) => {
          return {
            ...acc,
            ...(item?.subordinates || []).reduce(
              (accs, subitem) => ({
                ...accs,
                [subitem.id || '']: item
              }),
              {} as { [key: string]: ISelectOption }
            )
          }
        },
        {} as { [key: string]: ISelectOption }
      )

      const selectedPCMap = values.reduce(
        (acc, item) => {
          const parent = cpMap[item.value]
          return parent ? { ...acc, [parent.value]: [...(acc[parent.value] || []), item] } : acc
        },
        {} as { [key: string]: ISelectOption[] }
      )

      return values.map((item) => {
        const parent = cpMap[item.value]
        const currentChilds = data.parentChildsMap?.[item?.value] || []
        const allSelectedChilds = selectedPCMap[item.value] || []
        const allParentsChild = data.parentChildsMap?.[parent?.value || ''] || []
        const allSelectedParentsChild = selectedPCMap[parent?.value || ''] || []

        const isShowAll =
          (props?.value as ISelectOption[])?.length === data?.collection?.length && !data.isSearch

        return (item.subordinates || []).length > 0
          ? {
              ...item,
              parentId: parent?.value,
              chipHidden: isShowAll || currentChilds.length > (allSelectedChilds?.length || 0)
            }
          : parent
            ? {
                ...item,
                parentId: parent?.value,
                chipHidden: isShowAll || allParentsChild.length === allSelectedParentsChild.length
              }
            : {
                ...item,
                chipHidden: isShowAll
              }
      })
    },
    [data, props.value]
  )

  useEffect(() => {
    const propValue = props.value as ISelectOption[] | undefined
    const isShowAll =
      data?.collection?.length > 0 &&
      propValue?.length === data?.collection?.length &&
      !data.isSearch

    if (props.selectAllChange) {
      props.selectAllChange(isShowAll)
    }
  }, [props.value, data?.collection?.length, data.isSearch, props.selectAllChange])

  const propValue = props.value as ISelectOption[] | undefined
  const isShowAll =
    data?.collection?.length > 0 && propValue?.length === data?.collection?.length && !data.isSearch

  return loaded ? (
    <AsyncMultipleSearchWithSelect
      {...props}
      value={
        isShowAll
          ? props.showSelectOptionAll
            ? [
                ...shortTerningValue(propValue),
                {
                  value: '0',
                  supportingObj: {
                    name: isCompanyKind
                      ? `${t('settings:team:allTeams')}`
                      : `${t('settings:departments:allDepartments')}`
                  },
                  chipHidden: data.collection.length > (propValue?.length || 0)
                }
              ]
            : [...(propValue || [])]
          : shortTerningValue(propValue)
      }
      promiseOptions={promiseOptions}
      onChange={onChange}
      classNameOverride={{
        loadingIndicator: 'p-0! w-[10px]!',
        menuList: 'max-h-[367px]!',
        loadingMessage: `${t('label:loading')}`,
        noOptionsMessage: `${t('label:noOptions')}`,
        ...props.classNameOverride
      }}
      menuHeight={375}
    />
  ) : undefined
}

export default AsyncMultipleSearchWithSelectOptionGroup
